import type { ProfileInfo } from '../../../../core/services/profile-data.service';

/**
 * Everything the identity header asserts about the viewer. Produced only when
 * all of it is actually known — see `viewerIdentity` for why there is no
 * partially-populated variant.
 */
export interface ViewerIdentity {
  /** Non-empty. A blank name is an UNKNOWN name, not a name. */
  readonly name: string;
  /** Derived from `name`, so it exists exactly when the name does. */
  readonly initials: string;
  /** Job title / department, else the email. `null` when none is known. */
  readonly subtitle: string | null;
}

/**
 * Derive the identity header's content, or `null` when the viewer is NOT KNOWN.
 *
 * This replaces three computeds that each guessed:
 *
 *     name     ?? 'User'      // a fabricated identity in the viewer's own slot
 *     initials ?? '?'
 *     subtitle ?? ''
 *
 * The panel is one arm of the shell's panel switch, so it mounts the moment it
 * opens and `loadAll()` resolves afterwards; a failed profile query never
 * resolves it at all. In both windows the header rendered
 * `<h2 class="pp__name">User</h2>` — a plausible-looking name, in the position
 * reserved for a measured one, with nothing to distinguish it from the real
 * thing. "User" is not a fallback, it is a claim about who is signed in.
 *
 * BLANK COUNTS AS UNKNOWN. `ProfileInfo.name` is typed `string` but the wire
 * can carry `''`, which produced an empty `<h2>` and an initial-less avatar.
 * A whitespace-only name is treated the same as no profile: the header is not
 * rendered. The email is deliberately NOT promoted into the name slot — it is
 * known data, but substituting it for the name is still answering a question
 * we were not asked. It stays where it already belonged, in the subtitle.
 */
export function viewerIdentity(
  profile: ProfileInfo | null | undefined,
): ViewerIdentity | null {
  if (!profile) return null;

  const name = (profile.name ?? '').trim();
  if (!name) return null;

  const parts = name.split(/\s+/).filter(Boolean);
  const initials =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.charAt(0).toUpperCase();

  const facts: string[] = [];
  const jobTitle = profile.jobTitle?.trim();
  const department = profile.department?.trim();
  if (jobTitle) facts.push(jobTitle);
  if (department) facts.push(department);

  const subtitle = facts.length
    ? facts.join(' · ')
    : profile.email?.trim() || null;

  return { name, initials, subtitle };
}
